import type { ListEligibleMembershipsResponse } from '@wix/ambassador-ecom-memberships-spi-host-v1-membership/types';
import type { ControllerFlowAPI, Experiments } from '@wix/yoshi-flow-editor';
import settingsParams from '../../../../components/BookingsForm/settingsParams';
import { ExperimentsConsts } from '../../../../consts/experiments';
import {
  PaymentMethod,
  ReservedPaymentOptionIds,
} from '../../../../types/types';
import type { ServicePayment } from '../../../mappers/serviceMapper/utils/payment.mapper';
import type { CoveringPurchasableBenefit } from '../../benefitCoverage/benefitCoverage';
import {
  buildBuyPlanAndBookOptionId,
  DepositOrFullAmountOptionIds,
  getUniquePurchasablePlans,
  isDepositOrFullAmountFeatureEnabled,
  isOfferedAsOneTimeOnly,
  isOfferedAsPricingPlanOnly,
  isServiceHavePricingPlans,
} from '../utils';
import { isPlanAndBookEnabled } from '../../plans/utils';

export const getDefaultPaymentOptionId = ({
  settings,
  servicePayment,
  memberships,
  isPricingPlanInstalled,
  isDynamicPricingCustomOptions,
  hasEligiblePlans,
  experiments,
  paymentTypes,
  purchasableBenefits,
  excludePricingPlanOptions = false,
  isPlanAndBookBlocked,
  isPricingPlansMigratedToEcom,
}: {
  settings: ControllerFlowAPI['settings'];
  servicePayment: ServicePayment;
  isPricingPlanInstalled: boolean;
  memberships?: ListEligibleMembershipsResponse;
  isDynamicPricingCustomOptions?: boolean;
  hasEligiblePlans?: boolean;
  experiments?: Experiments;
  paymentTypes?: PaymentMethod[];
  purchasableBenefits?: CoveringPurchasableBenefit[];
  excludePricingPlanOptions?: boolean;
  /** ECP-12919, booking-level — see bookingRequiresStoredPaymentMethod. */
  isPlanAndBookBlocked?: boolean;
  /** Whether the site's Pricing Plans are migrated to eCom — see isPlanAndBookEnabled. */
  isPricingPlansMigratedToEcom?: boolean;
}) => {
  // Plan & Book: where the legacy flow defaults to the generic "Buy a plan" entry,
  // default to the first concrete purchasable plan option instead.
  // ECP-12919 (see bookingRequiresStoredPaymentMethod): when Plan & Book is hidden for the
  // booking, default to the first plan's plain "Buy a plan" id instead.
  const buyPlanDefaultOptionId = () => {
    if (
      isPlanAndBookEnabled({ experiments, isPricingPlansMigratedToEcom }) &&
      purchasableBenefits?.length
    ) {
      if (isPlanAndBookBlocked) {
        const firstPlanId =
          getUniquePurchasablePlans(purchasableBenefits)[0]?.catalogReference
            ?.catalogItemId;
        if (firstPlanId) {
          return firstPlanId;
        }
      } else {
        const { plan, benefit } = purchasableBenefits[0];
        return buildBuyPlanAndBookOptionId(
          plan.catalogReference?.catalogItemId,
          benefit.benefitId,
        );
      }
    }
    return ReservedPaymentOptionIds.BuyAPricingPlan;
  };

  const getDefaultSingleSessionPaymentOptionId = () =>
    isDepositOrFullAmountFeatureEnabled({
      experiments,
      fullUpfrontPaymentAllowed:
        servicePayment.paymentDetails.fullUpfrontPaymentAllowed,
      deposit: servicePayment.paymentDetails.minCharge,
      paymentTypes: paymentTypes ?? [],
    })
      ? DepositOrFullAmountOptionIds.Deposit
      : ReservedPaymentOptionIds.SingleSession;

  if (
    servicePayment.paymentDetails.subscriptionInfo &&
    experiments?.enabled(ExperimentsConsts.SubscriptionPricingUoU)
  ) {
    return ReservedPaymentOptionIds.Subscription;
  }

  if (isDynamicPricingCustomOptions) {
    return getDefaultSingleSessionPaymentOptionId();
  }
  const planId = excludePricingPlanOptions
    ? undefined
    : memberships?.eligibleMemberships?.[0]?.id; // todo: change to Selected Plan when implemented

  if (planId) {
    return planId;
  }
  if (
    excludePricingPlanOptions ||
    isOfferedAsOneTimeOnly(servicePayment) ||
    !isPricingPlanInstalled
  ) {
    return getDefaultSingleSessionPaymentOptionId();
  }
  if (isOfferedAsPricingPlanOnly(servicePayment)) {
    return buyPlanDefaultOptionId();
  }

  const selected =
    [
      PaymentMethod.SINGLE,
      PaymentMethod.ONLINE,
      PaymentMethod.OFFLINE,
    ].includes(settings.get(settingsParams.defaultPaymentMethod)) ||
    !isServiceHavePricingPlans(servicePayment) ||
    !hasEligiblePlans
      ? getDefaultSingleSessionPaymentOptionId()
      : buyPlanDefaultOptionId();
  return selected;
};
